<script setup lang="ts">
import { computed } from 'vue';

import analytics from '@/analytics';
interface Props {
  id: string;
  type: 'primary' | 'secondary' | 'ghost' | 'danger' | 'warning' | 'success' | 'text' | 'link' | 'icon' | 'primary-alt';
  onClick?: (e?: Event) => void;
  size?: 'x-small' | 'small' | 'medium' | 'large';
  rounded?: 'none' | 'small' | 'medium' | 'large';
  disabled?: boolean;
  loading?: boolean;
  analyticsEvent?: string;
  analyticsProperties?: Record<string, any>;
  customStyle?: string;
  useAnchorTag?: boolean;
  anchorTagProps?: Record<string, any>;
  tooltip?: string;
  tooltipPosition?:
    | 'tooltip-bottom-left'
    | 'tooltip-bottom-right'
    | 'tooltip-top-center'
    | 'default'
    | 'tooltip-top-right';
  ariaLabel?: string;
}

const props = withDefaults(defineProps<Props>(), {
  size: 'medium',
  analyticsEvent: 'cta-clicked',
  customStyle: '',
  useAnchorTag: false,
  tooltipPosition: 'default'
});

const emit = defineEmits<{
  (e: 'click', event?: Event): void;
}>();

const getButtonStyle = () => {
  switch (props.type) {
    case 'primary':
      return 'bg-themePrimaryColor text-themePrimaryTextColor ';
    case 'primary-alt':
      return 'bg-themePrimaryTextColor text-themePrimaryColor brightness-90';
    case 'secondary':
      return 'bg-themeSecondaryColor text-themeSecondaryTextColor hover:brightness-125';
    case 'ghost':
      return 'border border-themePrimaryTextColor/20 bg-transparent text-themePrimaryTextColor ';
    case 'danger':
      return 'bg-danger text-white';
    case 'warning':
      return 'bg-warning text-white';
    case 'success':
      return 'bg-success text-white';
    case 'text':
      return 'bg-transparent text-primary';
    case 'link':
      return 'bg-transparent text-themePrimaryTextColor underline !p-0';
    default:
      return 'bg-primary text-white';
  }
};

const getButtonRoundedStyle = () => {
  switch (props.rounded) {
    case 'none':
      return '';
    case 'small':
      return 'rounded-sm';
    case 'medium':
      return 'rounded-md';
    case 'large':
      return 'rounded-lg';
    default:
      return 'rounded';
  }
};

const buttonClasses = computed(() => {
  const baseClass =
    'inline-flex items-center justify-center outline-none focus:ring-0 btn-text transition-colors brightness-[0.85] hover:brightness-100 focus:brightness-100 ';

  const sizeClass = {
    'x-small': 'p-0 text-3xs',
    small: 'px-2 py-1 text-2xs',
    medium: 'px-2 py-1 text-2xs md:px-4 md:py-2 md:text-xs',
    large: 'px-6 py-3 '
  }[props.size];

  const disabledClass = props.disabled ? 'brightness-50 cursor-not-allowed' : '';

  return `${baseClass} ${getButtonStyle()} ${getButtonRoundedStyle()} ${sizeClass} ${disabledClass}`;
});

const handleClick = (e?: Event) => {
  // TODO: Remove this and only use the emit when the button is clicked @Pankaj
  if (props.onClick && typeof props.onClick === 'function') {
    props.onClick(e);
  } else {
    emit('click', e);
  }
  analytics.track(props.analyticsEvent, { id: props.id, ...props.analyticsProperties });
};
</script>

<template>
  <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
  <a
    v-if="useAnchorTag"
    :id="id"
    :data-testid="id"
    :class="{ [buttonClasses]: true, [props.customStyle]: !!props.customStyle, [tooltipPosition]: !!tooltipPosition }"
    :disabled="disabled || loading"
    :data-tooltip="tooltip"
    data-tooltip-multiline="true"
    :aria-label="props.ariaLabel"
    v-bind="anchorTagProps"
    @click="handleClick"
    @keydown.enter="handleClick"
  >
    <slot />
  </a>
  <button
    v-else
    :id="id"
    :data-testid="id"
    :class="{ [buttonClasses]: true, [props.customStyle]: !!props.customStyle, [tooltipPosition]: !!tooltipPosition }"
    :disabled="disabled || loading"
    :data-tooltip="tooltip"
    data-tooltip-multiline="true"
    :aria-label="props.ariaLabel"
    @click="handleClick"
  >
    <slot />
  </button>
</template>
